import { ContractItem } from '~/data/contract';

import { parseFromInput } from '@vestigas/utils';

/*
  Calculates the total price of a contract item based on the quantity ordered, the gross price per quantity, and the gross price.
  @param item - The contract item.
  @param locale - The locale to use for parsing the numbers.
  @returns The total price, or `undefined` when the item has no ordered quantity (an explicit 0 is a defined quantity).
*/
export function calculateContractItemTotalPrice(
  item: ContractItem,
  locale: string = 'de',
): number | undefined {
  const q = parseFromInput(item.quantityOrdered, { locale });

  if (q === null) {
    return undefined;
  }

  const ppq = parseFromInput(item.grossPricePerQuantity, { locale }) || 1;
  const gp = parseFromInput(item.grossPrice, { locale }) || 0;

  return (q / ppq) * gp;
}

/*
  Sums the total prices of all items that have an ordered quantity.
  @returns The sum, or `undefined` when no item has an ordered quantity (e.g. a framework contract).
*/
export function calculateContractTotalPrice(
  items: ContractItem[] | null | undefined,
  locale: string = 'de',
): number | undefined {
  const totals = (items ?? [])
    .map((item) => calculateContractItemTotalPrice(item, locale))
    .filter((total): total is number => total !== undefined);

  return totals.length > 0
    ? totals.reduce((sum, total) => sum + total, 0)
    : undefined;
}
